import { ButtonSize, TertiaryButton } from '@ardoq/button';
import { ContextDropdown } from 'canvas/contextToolBar/viewpointToolBar/toViewpointToolBarProps';
import { ContextSelector } from '@ardoq/component-hierarchies';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { FlexBox } from '@ardoq/layout';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { Icon, IconName } from '@ardoq/icons';
import { Text } from '@ardoq/typography';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { colors } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';

type ViewpointToolbarContextDropdownProps = ContextDropdown & {
  i18nLocale: I18nLocale;
  locale: Locale;
};

const ViewpointToolbarContextDropdown = (
  props: ViewpointToolbarContextDropdownProps
) => {
  const {
    i18nLocale,
    locale,
    currentContext,
    query,
    startSetComponentIds,
    onContextDropdownComponentSelected,
    onSelectAll,
    tracking,
  } = props;
  if (currentContext.isGraphSelection) {
    return (
      <FlexBox
        paddingLeft="small"
        marginRight="xsmall"
        gap="small"
        align="center"
      >
        <Text color="textSubtle" variant="caption">
          {t(i18nLocale, 'canvas:contextToolbarSelectionLabel', 'Selection')}
        </Text>
        <FlexBox gap="xsmall" align="center">
          {currentContext.representationData ? (
            <FlexBox
              borderWidth={1}
              borderColor="borderSubtle"
              borderRadius="full"
              backgroundColor="bgDefault"
              padding="xxsmall"
              maxWidth="200px"
              overflow="hidden"
            >
              <ComponentRepresentation
                iconSize={16}
                {...currentContext.representationData}
              />
            </FlexBox>
          ) : (
            <Icon
              iconSize={16}
              color={colors.icon}
              iconName={IconName.COMPONENT}
            />
          )}
          <FlexBox maxWidth="200px">
            <Text truncate variant="text2Bold">
              {currentContext.label}
            </Text>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  }
  return (
    <FlexBox paddingLeft="small" gap="small" align="center">
      <Text color="textSubtle" variant="caption">
        {t(i18nLocale, 'canvas:contextToolbarContextLabel', 'Context')}
      </Text>
      <ContextSelector
        selectedCount={currentContext.selectedIds.length}
        componentQuery={query}
        selectedComponentIds={startSetComponentIds}
        hasSearchInputInDropdown
        minDropdownSize={DropdownSize.L}
        onComponentsSelected={(scopeData, isSelectAll) => {
          if (isSelectAll) {
            onSelectAll();
            return;
          }
          onContextDropdownComponentSelected(scopeData.scopeComponents);
        }}
        tracking={tracking}
        i18nLocale={i18nLocale}
        locale={locale}
      >
        <TertiaryButton
          buttonSize={ButtonSize.SMALL}
          aria-label={t(
            i18nLocale,
            'canvas:contextToolbarChangeContext',
            { label: currentContext.label },
            `Change context: ${currentContext.label}`
          )}
        >
          <FlexBox paddingLeft="xsmall" gap="xsmall" align="center">
            <FlexBox gap="xsmall" align="center" maxWidth="200px">
              {currentContext.representationData ? (
                <ComponentRepresentation
                  iconSize={16}
                  {...currentContext.representationData}
                />
              ) : (
                <Icon
                  iconSize={16}
                  color={colors.icon}
                  iconName={IconName.COMPONENT}
                />
              )}
              <TruncatedTextPopover>
                {currentContext.label}
              </TruncatedTextPopover>
            </FlexBox>
            <Icon iconName={IconName.CHEVRON_DOWN} />
          </FlexBox>
        </TertiaryButton>
      </ContextSelector>
    </FlexBox>
  );
};

export { ViewpointToolbarContextDropdown };
